import { useEffect, useMemo, useState } from "react";
import { toggleTheme } from "../../store/slices/uiSlice";

import {
  Moon,
  Sun,
  Bell,
  Globe,
  DollarSign,
  Mail,
  Smartphone,
  Shield,
  RefreshCw,
  Check,
  ArrowLeft,
  Save,
  LogIn,
  LogInIcon,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Loader from "@/components/loader/Loader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import {
  fetchSettings,
  persistSettings,
} from "@/store/actions/settingsActions";
import { fetchCurrencies } from "@/store/actions/currencyActions";
import { queryClient } from "@/services/queryClient";
import { useCountries, useProfile } from "@/hooks";
import type { Currency } from "@/interfaces/currency";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import SecurityPage from "../../components/user/SecurityPage";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { makeRequest } from "@/utils/makeRequest";
import { normalizeCurrency, removeTrailingToken } from "@/utils";
import PaymentMethod from "@/components/user/PaymentMethod";
import { CustomSelect } from "@/components/shared/CustomSelect";

interface Preference {
  language: string;
  currency: string;
  timezone: string;
}

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

const SettingsSection = ({ title, children }: SettingsSectionProps) => (
  <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
    <div className="p-4 border-b border-border">
      <h3 className="font-semibold">{title}</h3>
    </div>
    <div className="p-4 space-y-4">{children}</div>
    <div className="p-4 border-t border-border bg-muted/50 flex justify-end"></div>
  </div>
);

const ToggleField = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex items-center justify-between p-4 bg-muted rounded-xl cursor-pointer">
    <div>
      <p className="font-medium">{label}</p>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>

    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
        checked ? "bg-yellow-500" : "bg-muted-foreground/30",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  </label>
);

const SettingsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { theme } = useAppSelector((state) => state.ui);
  const { currencies } = useAppSelector((state) => state.currency);
  const { data: countries } = useCountries();
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id;

  const { setting, isLoading } = useAppSelector((state) => state.setting);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [tempSelection, setTempSelection] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [phoneLocal, setPhoneLocal] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") ?? "profile";

  // const nationality =countries?.finf(country=>country.id===profile?.na)

  const handleCountryChange = (option: any) => {
    const countryId = option?.value ?? null;
    setSelectedCountry(countryId);

    const country = countries?.find((c: any) => c.id === countryId);

    if (country?.dialCode) {
      // reset phone when country changes
      setPhoneLocal("");
    }
  };

  const options = countries?.map((country: any) => {
    return {
      value: country.id,
      label: country.name,
      iso2: country.iso2,
    };
  });

  const formatOptionLabel = (option: any) => {
    const iso = option.iso2?.toLowerCase();

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={`https://flagcdn.com/w40/${iso}.png`}
          alt={option.label}
          width={20}
          height={14}
          style={{ objectFit: "cover", borderRadius: 2 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  const [notifications, setNotifications] = useState({
    tradeAlerts: false,
    priceAlerts: false,
    securityAlerts: false,
    emailAlerts: false,
    loginAlerts: false,
    pushNotifications: false,
  });

  const [preferencesState, setPreferencesState] = useState({
    language: "",
    currencyId: "",
    timezone: "",
  });

  const [currencySearch, setCurrencySearch] = useState("");

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    username: "",
    middleName: "",
    phoneNumber: "",
    email: "",
    role: "",
    loading: false,
  });

  const {
    data,
    isLoading: isLoadingProfile,
    isError,
    refetch,
  } = useProfile(userId as string);

  console.log("PROFILE", data);

  useEffect(() => {
    if (showCurrencyModal) {
      document.querySelector<HTMLInputElement>("input")?.focus();
    }
  }, [showCurrencyModal]);

  useEffect(() => {
    if (!data || !countries) return;

    setProfile({
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      username: data.username ?? "",
      middleName: data.middleName ?? "",
      phoneNumber: data?.phoneNumber ?? "",
      email: data.email ?? "",
      role: data.role ?? "",
      loading: false,
    });

    // 🔥 PREPOPULATE COUNTRY FROM PROFILE
    const matchedCountry = countries.find((c: any) => c.id === data.countryId);

    if (matchedCountry) {
      setSelectedCountry(matchedCountry.id);

      // remove dial code from phone if backend stored full number
      const dial = matchedCountry.dialCode || "";
      const rawPhone = data.phoneNumber?.replace(dial, "") || "";

      setPhoneLocal(rawPhone);
    }
  }, [data, countries]);

  useEffect(() => {
    dispatch(fetchSettings());
    dispatch(fetchCurrencies());
  }, [dispatch]);

  useEffect(() => {
    if (!setting) return;

    setNotifications({
      tradeAlerts: setting.tradeAlerts,
      priceAlerts: setting.priceAlerts,
      securityAlerts: setting.securityAlerts,
      emailAlerts: setting.emailAlerts,
      loginAlerts: setting.loginAlerts,
      pushNotifications: setting.pushNotifications,
    });

    setPreferencesState({
      language: setting.language,
      currencyId: setting.currencyId,
      timezone: setting.timezone,
    });
  }, [setting]);

  const preferences: Preference = useMemo(
    () => ({
      language: preferencesState.language || "en-US",
      currency: preferencesState.currencyId || "NGN",
      timezone: preferencesState.timezone || "Africa/Lagos",
    }),
    [preferencesState],
  );

  const languages = [
    {
      code: "en-US",
      name: "English (US)",
      flag: "🇺🇸",
    },
    {
      code: "en-GB",
      name: "English (UK)",
      flag: "🇬🇧",
    },
    {
      code: "fr",
      name: "Français",
      flag: "🇫🇷",
    },
    // {
    //   code: "es",
    //   name: "Español",
    //   flag: "🇪🇸",
    // },
    // {
    //   code: "de",
    //   name: "Deutsch",
    //   flag: "🇩🇪",
    // },
    // {
    //   code: "pt",
    //   name: "Português",
    //   flag: "🇵🇹",
    // },
    // {
    //   code: "ar",
    //   name: "العربية",
    //   flag: "🇸🇦",
    // },
    // {
    //   code: "zh",
    //   name: "中文",
    //   flag: "🇨🇳",
    // },
    // {
    //   code: "ja",
    //   name: "日本語",
    //   flag: "🇯🇵",
    // },
    // {
    //   code: "yo",
    //   name: "Yorùbá",
    //   flag: "🇳🇬",
    // },
    // {
    //   code: "ig",
    //   name: "Igbo",
    //   flag: "🇳🇬",
    // },
    // {
    //   code: "ha",
    //   name: "Hausa",
    //   flag: "🇳🇬",
    // },
  ];

  const timezones = [
    {
      code: "Africa/Lagos",
      name: "West Africa Time (WAT)",
      offset: "GMT+1",
      cities: "Lagos, Accra, Abidjan",
    },
    {
      code: "Africa/Johannesburg",
      name: "South Africa Standard Time (SAST)",
      offset: "GMT+2",
      cities: "Johannesburg, Cairo, Nairobi",
    },
    {
      code: "Europe/London",
      name: "Greenwich Mean Time (GMT)",
      offset: "GMT+0",
      cities: "London, Dublin, Lisbon",
    },
    {
      code: "Europe/Paris",
      name: "Central European Time (CET)",
      offset: "GMT+1",
      cities: "Paris, Berlin, Rome",
    },
    {
      code: "America/New_York",
      name: "Eastern Time (ET)",
      offset: "GMT-5",
      cities: "New York, Toronto, Miami",
    },
    {
      code: "America/Chicago",
      name: "Central Time (CT)",
      offset: "GMT-6",
      cities: "Chicago, Mexico City, Dallas",
    },
    {
      code: "America/Los_Angeles",
      name: "Pacific Time (PT)",
      offset: "GMT-8",
      cities: "Los Angeles, Vancouver, Seattle",
    },
    {
      code: "Asia/Dubai",
      name: "Gulf Standard Time (GST)",
      offset: "GMT+4",
      cities: "Dubai, Abu Dhabi, Muscat",
    },
    {
      code: "Asia/Singapore",
      name: "Singapore Time (SGT)",
      offset: "GMT+8",
      cities: "Singapore, Hong Kong, Beijing",
    },
    {
      code: "Asia/Tokyo",
      name: "Japan Standard Time (JST)",
      offset: "GMT+9",
      cities: "Tokyo, Seoul, Osaka",
    },
    {
      code: "Australia/Sydney",
      name: "Australian Eastern Time (AET)",
      offset: "GMT+11",
      cities: "Sydney, Melbourne, Brisbane",
    },
    {
      code: "UTC",
      name: "Coordinated Universal Time (UTC)",
      offset: "GMT+0",
      cities: "Universal",
    },
  ];

  const getCurrentLanguage = () =>
    languages.find((l) => l.code === preferences.language);

  const getCurrentCurrency = () =>
    currencies.find((c: Currency) => c.id === preferences.currency);

  const getCurrentTimezone = () =>
    timezones.find((t) => t.code === preferences.timezone);

  const activeCountry = useMemo(() => {
    return countries?.find((c: any) => c.id === selectedCountry) || null;
  }, [countries, selectedCountry]);

  const updateProfile = async () => {
    setProfile((prev) => ({ ...prev, loading: true }));

    const { role, email, loading, ...rest } = profile;

    if (!selectedCountry) {
      toast.info("Please select a country");
      setProfile((prev) => ({ ...prev, loading: false }));

      return;
    }

    const country = countries?.find((c: any) => c.id === selectedCountry);

    if (!country) {
      toast.info("Invalid country selected");
      setProfile((prev) => ({ ...prev, loading: false }));

      return;
    }

    if (!phoneLocal || phoneLocal.length < 6) {
      toast.info("Enter a valid phone number");
      setProfile((prev) => ({ ...prev, loading: false }));

      return;
    }

    // simple numeric validation
    if (!/^\d+$/.test(phoneLocal)) {
      toast.info("Phone number must contain only digits");
      setProfile((prev) => ({ ...prev, loading: false }));

      return;
    }

    const fullPhone = `${country.dialCode}${phoneLocal}`;

    try {
      const res = await makeRequest.put("/user/me", {
        ...rest,
        countryId: selectedCountry,
        phoneNumber: fullPhone,
      });

      if (res) {
        queryClient.invalidateQueries({ queryKey: ["profile", userId] });
        toast.success("Profile updated");
      }

      setProfile((prev) => ({ ...prev, loading: false }));
    } catch (error) {
      toast.error(getErrorMessage(error));
      setProfile((prev) => ({ ...prev, loading: false }));
    }
  };

  const updateSettings = async (payload: Record<string, any>) => {
    try {
      const result = await dispatch(persistSettings(payload) as any);

      if (!result?.payload) {
        throw new Error("Failed");
      }
    } catch {}
  };

  const handleSavePreference = async (
    type: "language" | "currency" | "timezone",
  ) => {
    if (!tempSelection) {
      toast.error("Error", { description: "Please make a selection" });

      return;
    }

    setIsProcessing(true);

    try {
      const payload = {
        language: type === "language" ? tempSelection : preferences.language,
        currencyId: type === "currency" ? tempSelection : preferences.currency,
        timezone: type === "timezone" ? tempSelection : preferences.timezone,
      };

      await dispatch(persistSettings(payload) as any);

      setPreferencesState({
        language: payload.language,
        currencyId: payload.currencyId,
        timezone: payload.timezone,
      });

      queryClient.invalidateQueries({ queryKey: ["wallet", userId] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });

      toast.success("Success!", {
        description: `${type} updated successfully`,
      });

      if (type === "language") {
        setShowLanguageModal(false);
      }

      if (type === "currency") {
        setShowCurrencyModal(false);
      }

      if (type === "timezone") {
        setShowTimezoneModal(false);
      }

      setTempSelection("");
    } catch {
      toast.error("Error", { description: "Failed to update preferences" });
    } finally {
      setIsProcessing(false);
    }
  };

  const updateNotification = async (
    key: keyof typeof notifications,
    value: boolean,
  ) => {
    const previous = notifications;

    setNotifications((prev) => ({
      ...prev,
      [key]: value,
    }));

    try {
      await dispatch(
        persistSettings({
          [key]: value,
        }) as any,
      );

      toast.success("Settings updated");
    } catch (error) {
      setNotifications(previous);

      toast.error("Failed to update settings");
    }
  };

  if (isLoading || !setting || isLoadingProfile) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background lg:px-6 w-full mt-10">
      <Button
        className="hidden md:flex"
        variant="ghost"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
      {/* <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>

        <p className="text-muted-foreground">Manage your account preferences</p>
      </div> */}

      <Tabs
        value={tab}
        onValueChange={(value) => setSearchParams({ tab: value })}
        className="space-y-4 mt-4 mx-1"
      >
        <div className="relative mb-6">
          {/* Left Fade */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />

          {/* Right Fade */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />

          <div className="overflow-x-auto scrollbar-hide w-full">
            <TabsList className="w-ful justify-start bg-transparent p-0 h-auto overflow-x-auto whitespace-nowrap scrollbar-hide rounded-none">
              <div className="flex gap-2 min-w-max pb-2">
                <TabsTrigger
                  className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                  value="profile"
                >
                  Profile
                </TabsTrigger>
                <TabsTrigger
                  className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                  value="payment-methods"
                >
                  Payment Methods
                </TabsTrigger>
                <TabsTrigger
                  className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                  value="preferences"
                >
                  Preferences
                </TabsTrigger>
                <TabsTrigger
                  className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                  value="notifications"
                >
                  Notifications
                </TabsTrigger>
                <TabsTrigger
                  className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                  value="security"
                >
                  Security
                </TabsTrigger>
              </div>
            </TabsList>
          </div>
        </div>

        <TabsContent value="profile" className="space-y-4 -mx-4">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    className="h-12"
                    id="firstName"
                    value={profile.firstName}
                    disabled
                    onChange={(e) =>
                      setProfile({ ...profile, firstName: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    className="h-12"
                    id="lastName"
                    value={profile.lastName}
                    disabled
                    onChange={(e) =>
                      setProfile({ ...profile, lastName: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Username</Label>
                  <Input
                    className="h-12"
                    id="username"
                    // disabled={profile?.username}
                    value={profile.username}
                    onChange={(e) =>
                      setProfile({ ...profile, username: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="middleName">Middle Name</Label>
                  <Input
                    className="h-12"
                    id="middleName"
                    value={profile.middleName}
                    onChange={(e) =>
                      setProfile({ ...profile, middleName: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="middleName">Country of residence</Label>
                  <CustomSelect
                    options={options}
                    isClearable
                    isSearchable
                    value={
                      options?.find(
                        (opt: any) => opt.value === selectedCountry,
                      ) || null
                    }
                    onChange={handleCountryChange}
                    formatOptionLabel={formatOptionLabel}
                    getOptionLabel={(option: any) => option.label}
                    getOptionValue={(option: any) => option.value}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="flex items-center gap-2">
                    {/* DIAL CODE (READ ONLY) */}
                    <div className="h-11 px-3 flex items-center border rounded-md bg-muted text-sm">
                      {activeCountry?.dialCode || "+---"}
                    </div>

                    {/* PHONE INPUT */}
                    <Input
                      className="h-12"
                      id="phoneNumber"
                      placeholder="Enter phone number"
                      value={phoneLocal}
                      disabled={!activeCountry}
                      onChange={(e) => setPhoneLocal(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email (Read only) </Label>
                  <Input
                    className="h-12"
                    id="email"
                    value={profile.email}
                    disabled
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role (Read only) </Label>
                  <Input
                    className="h-12"
                    id="role"
                    value={profile?.role}
                    disabled
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button className="h-12" onClick={updateProfile}>
                  <Save className="w-4 h-4 mr-2" />
                  {profile.loading ? <Loader2 /> : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payment-methods" className="space-y-4 -mx-4">
          <PaymentMethod />
        </TabsContent>

        <TabsContent value="preferences" className="space-y-4 -mx-4">
          <Card>
            <CardContent className="flex flex-col gap-1">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    {theme === "dark" ? (
                      <Moon className="w-5 h-5 text-primary" />
                    ) : (
                      <Sun className="w-5 h-5 text-primary" />
                    )}
                  </div>

                  <div>
                    <p className="text-foreground font-medium">Theme</p>

                    <p className="text-xs text-muted-foreground">
                      {theme === "dark" ? "Dark Mode" : "Light Mode"}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => dispatch(toggleTheme())}
                  className={cn(
                    "relative w-14 h-7 rounded-full transition-colors",
                    theme === "dark" ? "bg-primary" : "bg-muted",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-1 w-5 h-5 rounded-full bg-white transition-all shadow-sm",
                      theme === "dark" ? "left-8" : "left-1",
                    )}
                  />
                </button>
              </div>
              {/* Language */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>

                  <div>
                    <p className="text-foreground font-medium">Language</p>

                    <p className="text-xs text-muted-foreground">
                      {getCurrentLanguage()?.flag} {getCurrentLanguage()?.name}
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTempSelection(preferences.language);

                    setShowLanguageModal(true);
                  }}
                >
                  Change
                </Button>
              </div>

              {/* Currency */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>

                  <div>
                    <p className="text-foreground font-medium">Currency</p>

                    <p className="text-xs text-muted-foreground">
                      {getCurrentCurrency()?.symbol}{" "}
                      {getCurrentCurrency()?.name}
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTempSelection(preferences.currency);

                    setShowCurrencyModal(true);
                  }}
                >
                  Change
                </Button>
              </div>

              {/* Timezone */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-primary" />
                  </div>

                  <div>
                    <p className="text-foreground font-medium">Time Zone</p>

                    <p className="text-xs text-muted-foreground">
                      {getCurrentTimezone()?.name}
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTempSelection(preferences.timezone);

                    setShowTimezoneModal(true);
                  }}
                >
                  Change
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4 -mx-4">
          <Card>
            <CardContent>
              {/* Notifications */}

              <div className="bg-card border border-border rounded-xl p-1 -mx-4">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  Notifications
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-primary" />
                      </div>

                      <div>
                        <p className="text-foreground font-medium">
                          Trade Notifications
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Get notified about trade updates
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        updateNotification(
                          "tradeAlerts",
                          !notifications.tradeAlerts,
                        )
                      }
                      className={cn(
                        "relative w-12 h-6 rounded-full transition-colors",
                        notifications.tradeAlerts
                          ? "bg-emerald-500"
                          : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                          notifications.tradeAlerts ? "left-7" : "left-1",
                        )}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-primary" />
                      </div>

                      <div>
                        <p className="text-foreground font-medium">
                          Price Alerts
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Get notified when prices change
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        updateNotification(
                          "priceAlerts",
                          !notifications.priceAlerts,
                        )
                      }
                      className={cn(
                        "relative w-12 h-6 rounded-full transition-colors",
                        notifications.priceAlerts
                          ? "bg-emerald-500"
                          : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                          notifications.priceAlerts ? "left-7" : "left-1",
                        )}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>

                      <div>
                        <p className="text-foreground font-medium">
                          Security Alerts
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Get notified about security events
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        updateNotification(
                          "securityAlerts",
                          !notifications.securityAlerts,
                        )
                      }
                      className={cn(
                        "relative w-12 h-6 rounded-full transition-colors",
                        setting.securityAlerts ? "bg-emerald-500" : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                          setting.securityAlerts ? "left-7" : "left-1",
                        )}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>

                      <div>
                        <p className="text-foreground font-medium">
                          Marketing Emails
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Receive promotional emails
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        updateNotification(
                          "emailAlerts",
                          !notifications.emailAlerts,
                        )
                      }
                      className={cn(
                        "relative w-12 h-6 rounded-full transition-colors",
                        setting.emailAlerts ? "bg-emerald-500" : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                          setting.emailAlerts ? "left-7" : "left-1",
                        )}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <LogInIcon className="w-5 h-5 text-primary" />
                      </div>

                      <div>
                        <p className="text-foreground font-medium">
                          Login Notifications
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Receive alerts when your account is accessed
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        updateNotification(
                          "loginAlerts",
                          !notifications.loginAlerts,
                        )
                      }
                      className={cn(
                        "relative w-12 h-6 rounded-full transition-colors",
                        setting.loginAlerts ? "bg-emerald-500" : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                          setting.loginAlerts ? "left-7" : "left-1",
                        )}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-primary" />
                      </div>

                      <div>
                        <p className="text-foreground font-medium">
                          Push Notifications
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Set push notifications
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        updateNotification(
                          "pushNotifications",
                          !notifications.pushNotifications,
                        )
                      }
                      className={cn(
                        "relative w-12 h-6 rounded-full transition-colors",
                        setting.pushNotifications
                          ? "bg-emerald-500"
                          : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                          setting.pushNotifications ? "left-7" : "left-1",
                        )}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4 -mx-4">
          <Card className="-mx-4">
            <CardContent className="space-y-4">
              <SecurityPage />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showLanguageModal} onOpenChange={setShowLanguageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Language</DialogTitle>
            <DialogDescription>
              Choose your preferred language
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={tempSelection}
            onValueChange={setTempSelection}
            className="space-y-3"
          >
            {languages.map((language) => (
              <label
                key={language.code}
                className="flex items-center justify-between border rounded-lg p-3 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span>{language.flag}</span>
                  <span>{language.name}</span>
                </div>

                <RadioGroupItem value={language.code} />
              </label>
            ))}
          </RadioGroup>

          <Button
            disabled={isProcessing}
            onClick={() => handleSavePreference("language")}
          >
            {isProcessing ? <Loader2 className="animate-spin" /> : "Save"}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={showCurrencyModal} onOpenChange={setShowCurrencyModal}>
        <DialogContent className="max-h-[80vh] p-0 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b">
            <DialogHeader>
              <DialogTitle>Select Currency</DialogTitle>
              <DialogDescription>
                Choose your preferred currency
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Sticky Search */}
          <div className="px-5 py-3 border-b bg-background/95 backdrop-blur">
            <Input
              placeholder="Search currency..."
              value={currencySearch}
              onChange={(e) => setCurrencySearch(e.target.value)}
              className="h-10 rounded-full bg-muted/40 border-muted focus-visible:bg-background"
            />
          </div>

          {/* Scrollable List */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
            <RadioGroup
              value={tempSelection}
              onValueChange={setTempSelection}
              className="space-y-2"
            >
              {currencies
                .filter(
                  (currency) =>
                    currency.name
                      .toLowerCase()
                      .includes(currencySearch.toLowerCase()) ||
                    currency.code
                      .toLowerCase()
                      .includes(currencySearch.toLowerCase()),
                )
                .map((currency: Currency) => (
                  <label
                    key={currency.id}
                    className={`
                flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-colors
                ${
                  tempSelection === currency.id
                    ? "bg-primary/10 border-primary/30"
                    : "hover:bg-muted/40"
                }
              `}
                  >
                    <div>
                      <p className="font-medium text-sm">
                        {removeTrailingToken(currency.name)} ({currency.symbol})
                      </p>

                      <p className="text-xs text-muted-foreground">
                        {normalizeCurrency(currency.code)}
                      </p>
                    </div>

                    <RadioGroupItem value={currency.id} />
                  </label>
                ))}
            </RadioGroup>
          </div>

          {/* Sticky Footer */}
          <div className="border-t px-5 py-4 bg-background/95 backdrop-blur">
            <Button
              disabled={isProcessing}
              onClick={() => handleSavePreference("currency")}
              className="w-full h-11 rounded-full"
            >
              {isProcessing ? (
                <Loader2 className="animate-spin h-4 w-4" />
              ) : (
                "Save Currency"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTimezoneModal} onOpenChange={setShowTimezoneModal}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Time Zone</DialogTitle>

            <DialogDescription>
              Choose your preferred timezone
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={tempSelection}
            onValueChange={setTempSelection}
            className="space-y-3"
          >
            {timezones.map((timezone) => (
              <label
                key={timezone.code}
                className="flex items-center justify-between border rounded-lg p-3 cursor-pointer"
              >
                <div>
                  <p className="font-medium">{timezone.name}</p>

                  <p className="text-xs text-muted-foreground">
                    {timezone.offset} • {timezone.cities}
                  </p>
                </div>

                <RadioGroupItem value={timezone.code} />
              </label>
            ))}
          </RadioGroup>

          <Button
            disabled={isProcessing}
            onClick={() => handleSavePreference("timezone")}
          >
            {isProcessing ? <Loader2 className="animate-spin" /> : "Save"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
